/**
 * Client for /api/reports/abc-mgmt/*. Definitions live on the server
 * (app/services/abc_management_service.py); nothing here recomputes a number.
 */
import api from '@/utils/api';

export interface Kpi {
  customers: number; cart_events: number; events_by_source: Record<string, number>;
  attempted: number; connected: number; dials: number; connected_calls: number; conversation_pct: number | null;
  agent_marked_connected: number; untouched: number; sale_converted_crm: number;
  orders_all: number; orders_all_valid: number; revenue_all_valid: number;
  orders: number; revenue: number; revenue_net: number; aov: number | null; orders_cancelled: number;
  orders_no_agent: number; revenue_no_agent: number; orders_other_agent: number; revenue_other_agent: number;
  abc_conversion_pct: number | null; connected_who_ordered: number; lead_to_conversion_pct: number | null;
  customers_ordered: number; customers_bought_on_website: number;
  avg_hours_lead_to_order: number | null; orders_within_1h_pct: number | null;
  prepaid_pct: number | null; cod_pct: number | null; ppcod_pct: number | null; cross_sell_pct: number | null;
  delivered: number; returned: number; in_transit: number; awaiting_pickup: number; in_flight: number;
  rto_pct: number | null; in_transit_pct: number | null; cancelled_pct: number | null; rto_maturing: boolean;
  carts_outside_hours_pct: number | null;
  crm_conversion_pct: number | null; cc_orders: number; cc_revenue: number;
  non_converted: number; connected_not_converted: number; not_connected: number; not_attempted: number; not_attempted_but_bought: number;
  prepaid_n: number; cod_n: number; ppcod_n: number; cancelled_n: number;
}
export interface DailyRow { date: string; customers: number; events: number; dials: number; connected: number; orders: number; revenue: number; cc_orders: number;
  attempted_customers: number; connected_customers: number; conversion_pct: number | null }
export interface HourRow { hour: number; customers: number; attempted: number; connected: number; conversation_pct: number | null; ordered: number }
export interface AgentRow {
  agent_id: number; days_worked: number; dials: number; connected_calls: number; customers_called: number; customers_connected: number;
  orders: number; revenue: number; aov: number | null; dials_per_day: number | null; connected_per_day: number | null;
  orders_per_day: number | null; abc_conversion_pct: number | null; rto_pct: number | null; qualified: boolean;
}
export interface ProductRow {
  product: string; category: string; customers: number; attempted: number; connected: number; conversation_pct: number | null;
  orders: number; revenue: number; cc_revenue: number; aov: number | null; abc_conversion_pct: number | null; rto_pct: number | null; qualified: boolean;
}
export interface Period {
  range: { from: string; to: string; days: number };
  kpi: Kpi; daily: DailyRow[]; hourly: HourRow[]; time_to_order: { bucket: string; orders: number; pct: number | null }[];
  not_converted_reasons: { reason: string; count: number }[]; agents: AgentRow[]; products: ProductRow[];
  dispositions: { l1: string; l2: string; count: number }[];
  thresholds: { min_days: number; min_connected_agent: number; min_connected_product: number };
}
export interface Summary {
  success: boolean; data_as_of: string; own_only: boolean; team_size: number;
  current: Period; previous: Period; agent_names: Record<string, string>; flags: string[]; definitions: Record<string, string>;
}
export interface Options {
  success: boolean; own_only: boolean; team_agents: { id: number; name: string; active: boolean }[];
  products: string[]; categories: string[]; cart_sources: string[];
}
export interface Filters {
  from: string; to: string; fromTime: string; toTime: string; agentId: number | null; teamOnly: boolean;
  product: string; category: string; cartSource: string;
}

const unwrap = <T,>(res: unknown): T => res as unknown as T;

export async function getOptions(): Promise<Options> {
  return unwrap<Options>(await api.req({ url: '/reports/abc-mgmt/options', method: api.Method.GET }));
}

export async function postSummary(f: Filters): Promise<Summary> {
  return unwrap<Summary>(await api.req({
    url: '/reports/abc-mgmt/summary', method: api.Method.POST,
    data: {
      from_date: f.from, to_date: f.to, from_time: f.fromTime || null, to_time: f.toTime || null,
      agent_id: f.agentId, team_only: f.teamOnly, product: f.product || null, category: f.category || null, cart_source: f.cartSource || null,
    },
  }));
}
